import { getApiInstance } from '../axiosInterceptor'
import axios from 'axios'

/**
 * Get API endpoint URL
 * @returns {string} The base API URL
 */
const getApiEndpoint = () => {
    return process.env.REACT_APP_API_ENDPOINT || 'https://gw.dev.apim.kmart.com.au/kolumbus/api/v2'
}
// Request deduplication tracker
const pendingRequests = {}

/**
 * Generate a unique request key for RFP list requests
 * @param {Object} params - Query parameters
 * @returns {string} - Unique request key
 */
const getRfpListRequestKey = (params = {}) => {
  return `rfp_list_${params.drafts || 'false'}_${params.page || ''}_${params.limit || ''}`
}

/**
 * Create a new RFP (POST /rfp/createRFP)
 * @param {Object} rfpData - RFP form data
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with RFP creation result
 */
export const createRFP = async (rfpData, msalInstance) => {
  try {
    const formData = new FormData()

    formData.append('selectedVendor', rfpData.selectedVendor || '')
    formData.append('invoiceReferenceNumber', rfpData.invoiceReferenceNumber || '')
    formData.append('transactionDateTime', rfpData.transactionDateTime || '')
    formData.append('transactionCategories', rfpData.transactionCategories || '')
    formData.append('currencyType', rfpData.currencyType || '')

    // Only append otherCurrencyType if currencyType is 'Others'
    if (rfpData.currencyType === 'Others' && rfpData.otherCurrencyType) {
      formData.append('otherCurrencyType', rfpData.otherCurrencyType)
    }

    formData.append('amount', rfpData.amount || '')
    formData.append('filePreparedFor', rfpData.filePreparedFor || '')
    formData.append('preferredPaymentMethod', rfpData.preferredPaymentMethod || '')
    formData.append('costCenterPurposeList', rfpData.costCenterPurposeList || '')
    formData.append('saveAsDraft', rfpData.saveAsDraft || 'false')

    if (rfpData.attachments && rfpData.attachments.length > 0) {
      rfpData.attachments.forEach((file) => {
        const fileObj = file.originFileObj || file
        formData.append('attachments', fileObj, fileObj.name)
      })
    }

    const apiUrl = `${getApiEndpoint()}/rfp/createRFP`
    const headers = { 'Accept': 'application/json' }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, formData, { headers })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      headers['Authorization'] = `Bearer ${token}`
      const response = await axios.post(apiUrl, formData, { headers })
      return response.data
    }
  } catch (error) {
    if (error.response) {
      console.error('Error creating RFP:', error.response.data)
    }
    throw error
  }
}

/**
 * Edit an existing RFP draft (POST /rfp/editRFP)
 * @param {string} transactionId - The RFP transaction ID to edit
 * @param {boolean} isDraft - Whether this is a draft (true) or submitted RFP (false)
 * @param {Object} updates - Updated RFP data
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with edit result
 */
export const editRFP = async (transactionId, isDraft, updates, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    const apiUrl = `${getApiEndpoint()}/rfp/editRFP`

    // Prepare updates - ensure costCenterPurposeList is an array
    const preparedUpdates = { ...updates }

    // Remove sapReferenceId from all edit operations
    if (preparedUpdates.sapReferenceId !== undefined) {
      delete preparedUpdates.sapReferenceId
    }

    // If costCenterPurposeList is a string, parse it to array
    if (preparedUpdates.costCenterPurposeList && typeof preparedUpdates.costCenterPurposeList === 'string') {
      try {
        preparedUpdates.costCenterPurposeList = JSON.parse(preparedUpdates.costCenterPurposeList)
      } catch (e) {
        console.error('Error parsing costCenterPurposeList:', e)
      }
    }

    const requestBody = {
      transactionId,
      isDraft: String(isDraft), // Convert to string as per API spec
      updates: preparedUpdates
    }

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, requestBody, { headers })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, requestBody, { headers })
      return response.data
    }
  } catch (error) {
    if (error.response) {
      console.error('Error editing RFP:', error.response.data)
    }
    throw error
  }
}

/**
 * Edit an existing draft RFP and save as draft (POST /rfp/editRFP)
 * This is a convenience wrapper for editing drafts
 * @param {string} transactionId - The RFP transaction ID to edit
 * @param {Object} updates - Updated RFP data
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with edit result
 */
export const editDraftRFP = async (transactionId, updates, msalInstance) => {
  return editRFP(transactionId, true, updates, msalInstance)
}

/**
 * Edit a submitted RFP (POST /rfp/editRFP)
 * This is a convenience wrapper for editing submitted RFPs
 * @param {string} transactionId - The RFP transaction ID to edit
 * @param {Object} updates - Updated RFP data (can include paymentStatus)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with edit result
 */
export const editSubmittedRFP = async (transactionId, updates, msalInstance) => {
  return editRFP(transactionId, false, updates, msalInstance)
}

/**
 * Fetch RFP list with server-side pagination (GET /rfp/fetchRFPList)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {Object} params - Query parameters object
 * @param {boolean} params.drafts - Whether to fetch drafts (default: false)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Number of items per page (default: 10)
 * @returns {Promise} - Promise that resolves with RFP list data
 */
export const fetchRFPList = async (msalInstance, params = {}) => {
  try {
    const queryParams = {
      drafts: params.drafts !== undefined ? params.drafts : false,
      page: params.page || 1,
      limit: params.limit || 10
    }

    const requestKey = getRfpListRequestKey(queryParams)

    if (pendingRequests[requestKey]) {
      return pendingRequests[requestKey]
    }

    const queryString = Object.entries(queryParams)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')

    const apiUrl = `${getApiEndpoint()}/rfp/fetchRFPList?${queryString}`
    const axiosInstance = getApiInstance(msalInstance)

    pendingRequests[requestKey] = axiosInstance.get(apiUrl)
      .then(response => {
        delete pendingRequests[requestKey]
        return response.data
      })
      .catch(error => {
        delete pendingRequests[requestKey]
        if (error.response) {
          console.error('Error fetching RFP list:', error.response.data)
        }
        throw error
      })

    return pendingRequests[requestKey]
  } catch (error) {
    throw error
  }
}

/**
 * Fetch a specific RFP draft by ID to get full details for editing
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {string} draftId - The draft ID to search for
 * @returns {Promise} - Promise that resolves with the specific draft data
 */
export const fetchRFPDraftById = async (msalInstance, draftId) => {
  try {
    if (!draftId) {
      throw new Error('Draft ID is required')
    }

    // Fetch drafts and search for the specific ID
    // We use a larger limit to ensure we can find the draft
    const response = await fetchRFPList(msalInstance, {
      drafts: true,
      page: 1,
      limit: 100 // Fetch more records to find the specific draft
    })

    if (response?.data) {
      // Find the specific draft by ID or transactionId
      const draft = response.data.find(item =>
        item._id === draftId ||
        item.transactionId === draftId ||
        item.id === draftId
      )

      if (draft) {
        return { data: draft }
      } else {
        throw new Error(`Draft with ID ${draftId} not found`)
      }
    } else {
      throw new Error('No draft data received from API')
    }
  } catch (error) {
    if (error.response) {
      console.error('Error fetching RFP draft by ID:', error.response.data)
    }
    throw error
  }
}

/**
 * Delete RFP draft (DELETE /rfp/deleteRFP)
 * @param {string} transactionId - The RFP transaction ID to delete
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with deletion result
 */
export const deleteRFPDraft = async (transactionId, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required for deletion')
    }

    const baseUrl = `${getApiEndpoint()}/rfp/deleteRFP`
    const apiUrl = `${baseUrl}?transactionId=${encodeURIComponent(transactionId)}`

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.delete(apiUrl, { headers })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.delete(apiUrl, { headers })
      return response.data
    }
  } catch (error) {
    if (error.response) {
      console.error('Error deleting RFP draft:', error.response.data)
    }
    throw error
  }
}

/**
 * Approve or Reject RFP (POST /rfp/approveRFP)
 * @param {string} rfpId - The RFP ID to approve/reject
 * @param {string} action - Action to perform: 'approve' or 'reject'
 * @param {string} approvalComment - Comment for rejection (required if action is 'reject')
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with approval result
 */
export const approveRejectRFP = async (rfpId, action, approvalComment = '', msalInstance) => {
  try {
    if (!rfpId) {
      throw new Error('RFP ID is required')
    }

    if (!action || !['approve', 'reject'].includes(action)) {
      throw new Error('Action must be either "approve" or "reject"')
    }

    if (action === 'reject' && !approvalComment) {
      throw new Error('Approval comment is required for rejection')
    }

    const apiUrl = `${getApiEndpoint()}/rfp/approveRFP`

    const requestBody = {
      rfpId,
      action
    }

    // Only include approvalComment if action is reject
    if (action === 'reject') {
      requestBody.approvalComment = approvalComment
    }

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, requestBody, { headers })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, requestBody, { headers })
      return response.data
    }
  } catch (error) {
    if (error.response) {
      console.error(`Error ${action}ing RFP:`, error.response.data)
    }
    throw error
  }
}

/**
 * Handle RFP attachments - upload or delete (POST /handleAttachments)
 * @param {string} txnId - The RFP transaction ID
 * @param {string} actionType - Action type: 'update' (for upload) or 'delete'
 * @param {string} attachmentId - Attachment ID (required for delete operations)
 * @param {File} newFile - File to upload (required for update operations)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with the operation result
 */
export const handleRfpAttachments = async (txnId, actionType, attachmentId = null, newFile = null, msalInstance) => {
  try {
    if (!txnId) {
      throw new Error('Transaction ID is required')
    }

    if (!actionType || (actionType !== 'update' && actionType !== 'delete')) {
      throw new Error('Valid action type (update or delete) is required')
    }

    if (actionType === 'delete' && !attachmentId) {
      throw new Error('Attachment ID is required for delete operations')
    }

    if (actionType === 'update' && !newFile) {
      throw new Error('File is required for update operations')
    }

    const apiUrl = `${getApiEndpoint()}/generic/handleAttachments`

    // Create form data for the request
    const formData = new FormData()

    // Add required fields
    formData.append('txnId', txnId)
    formData.append('type', 'rfp')

    if (actionType === 'delete') {
      formData.append('actionType', 'delete')
      formData.append('attachmentId', attachmentId)
    } else if (actionType === 'update') {
      formData.append('actionType', 'update')
      formData.append('newFile', newFile, newFile.name)
    }

    // Make the API request with appropriate headers
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, formData, {
        headers: {
          'Accept': 'application/json'
        }
      })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      return response.data
    }
  } catch (error) {
    console.error('Error handling RFP attachments:', error)
    throw error
  }
}

/**
 * Generate a pre-signed URL for an RFP attachment
 * @param {string} attachmentS3Path - The S3 path of the attachment
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise<string>} - Promise that resolves with the pre-signed URL
 */
export const generateRfpPresignedUrl = async (attachmentS3Path, msalInstance) => {
  try {
    if (!attachmentS3Path) {
      throw new Error('Attachment S3 path is required')
    }

    const apiUrl = `${getApiEndpoint().replace(/\/$/, '')}/generic/generatePreSignedUrl`
    const payload = {
      attachmentS3Path: attachmentS3Path
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, payload)
      return response.data.preSignedUrl
    } else {
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data.preSignedUrl
    }
  } catch (error) {
    console.error('Error generating pre-signed URL for RFP attachment:', error)
    throw error
  }
}

/**
 * Update RFP payment status (POST /rfp/editRFP)
 * @param {string} transactionId - The RFP transaction ID
 * @param {string} paymentStatus - Payment status: 'Y' or 'N'
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with update result
 */
export const updateRfpPaymentStatus = async (transactionId, paymentStatus, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    if (!paymentStatus || !['Y', 'N'].includes(paymentStatus)) {
      throw new Error('Payment status must be either "Y" or "N"')
    }

    // Use editSubmittedRFP (isDraft: false) with paymentStatus in updates
    const response = await editSubmittedRFP(
      transactionId,
      { paymentStatus },
      msalInstance
    )

    return response
  } catch (error) {
    console.error('Error updating RFP payment status:', error)
    throw error
  }
}

/**
 * Export RFP list as Excel file (GET /rfp/exportExcel?type=rfp)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {Object} params - Optional query parameters (e.g. searchTerm)
 * @returns {Promise<Blob>} - Promise that resolves with the Excel file blob
 */
export const exportRFPExcel = async (msalInstance, params = {}) => {
  try {
    const queryParts = ['type=rfp'] // Always send type=rfp for RFP exports

    if (params.searchTerm) {
      queryParts.push(`searchTerm=${encodeURIComponent(params.searchTerm)}`)
    }

    if (params.startDate) {
      queryParts.push(`startDate=${encodeURIComponent(params.startDate)}`)
    }

    if (params.endDate) {
      queryParts.push(`endDate=${encodeURIComponent(params.endDate)}`)
    }

    if (params.allStores) {
      queryParts.push('allStores=true')
    }

    const queryString = `?${queryParts.join('&')}`
    const apiUrl = `${getApiEndpoint()}/rfp/exportExcel${queryString}`

    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.get(apiUrl, {
      responseType: 'blob', // Must be blob to handle binary file
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream'
      }
    })

    return response.data // Blob
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text()
        const json = JSON.parse(text)
        const enhancedError = new Error(json.error || json.message || 'Export failed')
        enhancedError.status = error.response.status
        throw enhancedError
      } catch (parseErr) {
        if (parseErr.status) throw parseErr
        console.error('Error exporting RFP Excel:', error.response.data)
      }
    } else if (error.response) {
      console.error('Error exporting RFP Excel:', error.response.data)
    }
    throw error
  }
}

/**
 * Search RFP by search term (GET /rfp/searchRFP)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {string} searchTerm - Search term (e.g. RFP ID, vendor name)
 * @returns {Promise} - Promise that resolves with matching RFP data
 */
export const searchRFP = async (msalInstance, searchTerm) => {
  try {
    if (!searchTerm?.trim()) {
      throw new Error('Search term is required')
    }

    const apiUrl = `${getApiEndpoint()}/rfp/searchRFP?searchTerm=${encodeURIComponent(searchTerm.trim())}`
    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.get(apiUrl)
    return response.data
  } catch (error) {
    if (error.response) {
      console.error('Error searching RFP:', error.response.data)
    }
    throw error
  }
}

/**
 * Send RFP approval reminder email (POST /rfp/sendRFPReminder)
 * @param {string} transactionId - The RFP transaction ID
 * @param {string|number} storeId - Store ID header value
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with reminder result
 */
export const sendRfpApprovalReminder = async (transactionId, storeId, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    if (storeId === undefined || storeId === null || storeId === '') {
      throw new Error('Store ID is required')
    }

    const apiUrl = `${getApiEndpoint()}/rfp/sendRFPReminder`
    const requestBody = { transactionId }

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'storeid': String(storeId)
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, requestBody, { headers })
      return response.data
    } else {
      const token = localStorage.getItem('bearerToken')
      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, requestBody, { headers })
      return response.data
    }
  } catch (error) {
    if (error.response) {
      console.error('Error sending RFP approval reminder:', error.response.data)
    }
    throw error
  }
}